/**
 * 基本条件サマリー（業種 / 建築工程 / 募集職種 / アピールポイント / 雇用形態給与 / 資格）の
 * Alpine コンポーネント。編集はクライアント側で完結し、保存時に hidden input が直列化される。
 *
 * Step A: 業種・建築工程・募集職種・アピールポイントの選択モーダル + サマリー + 直列化。
 * Step B（別途）: 雇用形態/給与の2stepウィザード、資格エディタ。
 * （雇用形態/資格は本Stepでは値を保持するのみ＝サマリー表示と hidden 直列化）
 */
document.addEventListener('alpine:init', () => {
    // 基本条件の即時保存後に「AI生成ボタンの可否」「募集要項の参照表示(募集職種/給与)」を
    // リロード無しで更新するための共有state。初期値はサーバー描画値（window.__recruitBasicSync）。
    window.Alpine.store('recruitBasicSync', {
        ...(window.__recruitBasicSync || { aiCanGenerate: false, aiMessage: '', category: '', payrollSummary: '' }),
        _esc(s) {
            return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        },
        _nl2br(s) { return this._esc(s).replace(/\n/g, '<br>'); },
        categoryHtml() {
            return this.category ? this._nl2br(this.category) : '<span class="text-muted">未設定</span>';
        },
        payrollSummaryHtml() {
            return this.payrollSummary ? this._nl2br(this.payrollSummary) : '<span class="text-muted">未設定</span>';
        },
    });

    window.Alpine.data('recruitBasic', (init = {}) => ({
        // マスター名マップ（id -> 名称）
        oldMap: init.oldMap || {},
        middleMap: init.middleMap || {},
        driverMap: init.driverMap || {},
        tagMap: init.tagMap || {},
        empMap: init.empMap || {},
        payMap: init.payMap || {},
        payrollSettings: init.payrollSettings || {},
        masterQualTree: init.masterQualTree || [],

        // 選択状態
        checkboxes: init.checkboxes || {},
        newCheckboxes: init.newCheckboxes || {},
        otherNewCheckboxes: init.otherNewCheckboxes || {},
        driverCheckboxes: init.driverCheckboxes || {},
        tagCheckboxes: init.tagCheckboxes || {},
        recruitTypes: init.recruitTypes || [],
        qualifications: init.qualifications || [],
        jobTypes: init.jobTypes || '',

        // モーダル開閉
        modal: { oldInd: false, newInd: false, jobType: false, tag: false, emp: false, qual: false },

        // モーダル用一時状態
        tempCheckboxes: {},
        tempNewCheckboxes: {},
        tempOtherNewCheckboxes: {},
        tempDriverCheckboxes: {},
        tempTagCheckboxes: {},
        tempJobTypes: '',

        // ---- 汎用 ----
        selectedIds(obj) { return Object.keys(obj).filter((k) => obj[k]); },
        count(obj) { return this.selectedIds(obj).length; },
        names(obj, map) { return this.selectedIds(obj).map((id) => map[id]).filter(Boolean); },
        nf(n) { return Number(n || 0).toLocaleString(); },

        // ---- サマリー表示（リアクティブ） ----
        get oldIndustryNames() {
            // 旧業種 + 業種その他
            return this.names(this.checkboxes, this.oldMap)
                .concat(this.names(this.otherNewCheckboxes, this.middleMap));
        },
        get driverNames() { return this.names(this.driverCheckboxes, this.driverMap); },
        get ksenchikuNames() { return this.names(this.newCheckboxes, this.middleMap); },
        get tagNames() { return this.names(this.tagCheckboxes, this.tagMap); },
        get qualificationNames() {
            return this.qualifications.filter((q) => q && q.name).map((q) => q.name);
        },
        get activeRecruitTypes() {
            return this.recruitTypes.filter((t) => t && t.employment_type_id && t.employment_type_id != 0);
        },
        empLabel(t) {
            const emp = this.empMap[t.employment_type_id] || '';
            const pay = this.payMap[t.payment_type_id] || '';
            let s = `${emp}：${pay} ${this.nf(t.payroll)}円 〜`;
            if (t.max_payroll) s += ` ${this.nf(t.max_payroll)}円`;
            return s;
        },

        // ---- サマリーHTML（サーバー初期描画と同一マークアップ。FOUC防止） ----
        esc(s) {
            return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        },
        badge(cls, name) { return `<span class="${cls}">${this.esc(name)}</span>`; },
        muted() { return '<p class="text-muted">未設定</p>'; },
        get industryHtml() {
            const main = this.oldIndustryNames;
            const driver = this.driverNames;
            if (!main.length && !driver.length) return this.muted();
            let h = '<div class="industry-list-basic">';
            h += main.map((n) => this.badge('industry-badge', n)).join('');
            if (driver.length) {
                h += '<span style="margin: 0 4px;">／</span>';
                h += driver.map((n) => this.badge('industry-badge', n)).join('');
            }
            return h + '</div>';
        },
        get ksenchikuHtml() {
            const names = this.ksenchikuNames;
            if (!names.length) return this.muted();
            return '<div class="industry-list-basic">' + names.map((n) => this.badge('industry-badge', n)).join('') + '</div>';
        },
        get jobTypesHtml() {
            if (!this.jobTypes) return this.muted();
            return '<p style="white-space: pre-line;">' + this.esc(this.jobTypes) + '</p>';
        },
        get empHtml() {
            const types = this.activeRecruitTypes;
            if (!types.length) return this.muted();
            return types.map((t) => '<p>' + this.esc(this.empLabel(t)) + '</p>').join('');
        },
        get tagHtml() {
            const names = this.tagNames;
            if (!names.length) return this.muted();
            return '<div class="tag-list">' + names.map((n) => this.badge('tag-badge', n)).join('') + '</div>';
        },
        get qualHtml() {
            const names = this.qualificationNames;
            if (!names.length) return this.muted();
            return '<div class="qualification-list-basic">' + names.map((n) => this.badge('qualification-badge', n)).join('') + '</div>';
        },

        // ---- 業種モーダル（旧業種 + その他 + ドライバー。合計3つまで／ドライバー8つまで） ----
        openOldInd() {
            this.tempCheckboxes = { ...this.checkboxes };
            this.tempOtherNewCheckboxes = { ...this.otherNewCheckboxes };
            this.tempDriverCheckboxes = { ...this.driverCheckboxes };
            this.modal.oldInd = true;
        },
        oldTotalChecked() {
            return this.count(this.tempCheckboxes) + this.count(this.tempOtherNewCheckboxes);
        },
        toggleOldIndustry(id, mode) {
            if (mode === 'driver') {
                const checked = !!this.tempDriverCheckboxes[id];
                if (!checked && this.count(this.tempDriverCheckboxes) >= 8) return;
                this.tempDriverCheckboxes[id] = !checked;
                return;
            }
            const target = mode === 'other' ? this.tempOtherNewCheckboxes : this.tempCheckboxes;
            const checked = !!target[id];
            if (!checked && this.oldTotalChecked() >= 3) return;
            target[id] = !checked;
        },
        updateOldInd() {
            this.checkboxes = { ...this.tempCheckboxes };
            this.otherNewCheckboxes = { ...this.tempOtherNewCheckboxes };
            this.driverCheckboxes = { ...this.tempDriverCheckboxes };
            this.modal.oldInd = false;
            this.$nextTick(() => this.saveBasicNow());
        },

        // ---- 建築工程モーダル（3つまで） ----
        openNewInd() {
            this.tempNewCheckboxes = { ...this.newCheckboxes };
            this.modal.newInd = true;
        },
        toggleIndustry(id) {
            const checked = !!this.tempNewCheckboxes[id];
            if (!checked && this.count(this.tempNewCheckboxes) >= 3) return;
            this.tempNewCheckboxes[id] = !checked;
        },
        updateNewInd() {
            this.newCheckboxes = { ...this.tempNewCheckboxes };
            this.modal.newInd = false;
            this.$nextTick(() => this.saveBasicNow());
        },

        // ---- 募集職種モーダル（フリー入力） ----
        openJobType() { this.tempJobTypes = this.jobTypes; this.modal.jobType = true; },
        updateJobType() { this.jobTypes = this.tempJobTypes; this.modal.jobType = false; this.$nextTick(() => this.saveBasicNow()); },

        // ---- アピールポイントモーダル（複数選択） ----
        openTag() { this.tempTagCheckboxes = { ...this.tagCheckboxes }; this.modal.tag = true; },
        toggleTag(id) { this.tempTagCheckboxes[id] = !this.tempTagCheckboxes[id]; },
        updateTag() { this.tagCheckboxes = { ...this.tempTagCheckboxes }; this.modal.tag = false; this.$nextTick(() => this.saveBasicNow()); },

        // ドラッグ並替用の安定キー（Sortable + Alpine x-for 整合のため）
        _uidSeq: 0,
        _uid() { this._uidSeq += 1; return 'u' + this._uidSeq; },

        // =================================================================
        // 雇用形態/給与モーダル（2ステップウィザード）
        // =================================================================
        emp: {
            step: 1,
            editingIndex: null,
            editingData: { employment_type_id: 0, payment_type_id: 0, payroll: 0, max_payroll: null, payroll_input_type: 'range' },
            errorField: '',
            errorMessage: '',
        },
        tempRecruitTypes: [],

        empNameOf(t) { return this.empMap[t.employment_type_id] || ''; },
        payNameOf(t) { return this.payMap[t.payment_type_id] || ''; },

        resetEditingEmp() {
            this.emp.editingData = { employment_type_id: 0, payment_type_id: 0, payroll: 0, max_payroll: null, payroll_input_type: 'range' };
        },
        openEmp() {
            // 有効データのみコピー（並替用 _uid を付与）
            this.tempRecruitTypes = JSON.parse(JSON.stringify(this.recruitTypes))
                .filter((t) => t.employment_type_id && t.employment_type_id != 0)
                .map((t) => ({ ...t, _uid: this._uid() }));
            this.emp.step = 1;
            this.emp.editingIndex = null;
            this.emp.errorField = '';
            this.emp.errorMessage = '';
            this.modal.emp = true;
        },
        addNewEmp() {
            if (this.tempRecruitTypes.length >= 4) return;
            this.emp.editingIndex = null;
            this.resetEditingEmp();
            this.emp.errorField = '';
            this.emp.errorMessage = '';
            this.emp.step = 2;
        },
        editEmp(i) {
            const d = this.tempRecruitTypes[i];
            this.emp.editingIndex = i;
            this.emp.editingData = {
                employment_type_id: d.employment_type_id ?? 0,
                payment_type_id: d.payment_type_id ?? 0,
                payroll: d.payroll ?? 0,
                max_payroll: d.max_payroll ?? null,
                payroll_input_type: 'range',
            };
            this.emp.errorField = '';
            this.emp.errorMessage = '';
            this.emp.step = 2;
        },
        backEmpStep1() {
            this.emp.step = 1;
            this.emp.editingIndex = null;
            this.emp.errorField = '';
            this.emp.errorMessage = '';
            this.resetEditingEmp();
        },
        changeEmpPaymentType() {
            this.emp.editingData.payroll = 0;
            this.emp.editingData.max_payroll = null;
        },
        // 給与形態(1:月給,2:日給,3:時給)に応じた給与選択肢
        payrollOptions(paymentTypeId) {
            const map = { 1: 'monthly', 2: 'daily', 3: 'hourly' };
            const key = map[paymentTypeId];
            const s = key ? this.payrollSettings[key] : null;
            if (!s) return [];
            const opts = [];
            for (let i = s.min; i <= s.max; i += s.step) opts.push(i);
            return opts;
        },
        get empPayrollOptions() { return this.payrollOptions(this.emp.editingData.payment_type_id); },
        get empMaxPayrollOptions() {
            const min = Number(this.emp.editingData.payroll) || 0;
            return this.empPayrollOptions.filter((o) => o > min);
        },
        saveEmpStep2() {
            const d = this.emp.editingData;
            if (!d.employment_type_id || d.employment_type_id == 0) { this.emp.errorField = 'employment_type'; this.emp.errorMessage = '雇用形態を選択してください'; return; }
            if (!d.payment_type_id || d.payment_type_id == 0) { this.emp.errorField = 'payment_type'; this.emp.errorMessage = '給与形態を選択してください'; return; }
            if (!d.payroll || d.payroll == 0) { this.emp.errorField = 'payroll'; this.emp.errorMessage = '給与の下限を選択してください'; return; }
            this.emp.errorField = ''; this.emp.errorMessage = '';

            const saveData = {
                employment_type_id: d.employment_type_id,
                payment_type_id: d.payment_type_id,
                payroll: d.payroll,
                max_payroll: (d.max_payroll === '' ? null : d.max_payroll),
                _uid: (this.emp.editingIndex !== null ? this.tempRecruitTypes[this.emp.editingIndex]._uid : this._uid()),
            };
            if (this.emp.editingIndex !== null) {
                this.tempRecruitTypes[this.emp.editingIndex] = saveData;
            } else {
                this.tempRecruitTypes.push(saveData);
            }
            this.backEmpStep1();
        },
        deleteCurrentEmp() {
            if (this.emp.editingIndex !== null) {
                this.tempRecruitTypes.splice(this.emp.editingIndex, 1);
            }
            this.backEmpStep1();
        },
        reorderEmp(oldIndex, newIndex) {
            if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
            const arr = this.tempRecruitTypes;
            const item = arr.splice(oldIndex, 1)[0];
            arr.splice(newIndex, 0, item);
            // Sortableが動かしたDOMを破棄し、正しい配列順でAlpineに作り直させる
            const copy = arr.slice();
            this.tempRecruitTypes = [];
            this.$nextTick(() => { this.tempRecruitTypes = copy; });
        },
        updateEmp() {
            this.recruitTypes = JSON.parse(JSON.stringify(this.tempRecruitTypes));
            this.modal.emp = false;
            this.emp.step = 1;
            this.$nextTick(() => this.saveBasicNow());
        },

        // =================================================================
        // 歓迎する資格モーダル
        // =================================================================
        tempQualifications: [],

        openQual() {
            this.tempQualifications = JSON.parse(JSON.stringify(this.qualifications));
            if (this.tempQualifications.length === 0) {
                this.tempQualifications = [{ type: '', name: '', qualification_id: null }];
            }
            this.tempQualifications.forEach((q) => { q._uid = this._uid(); });
            this.modal.qual = true;
        },
        addTempQual() { this.tempQualifications.push({ type: '', name: '', qualification_id: null, _uid: this._uid() }); },
        removeTempQual(i) {
            if (this.tempQualifications.length <= 1) return;
            this.tempQualifications.splice(i, 1);
        },
        resetQualName(i) {
            if (this.tempQualifications[i]) {
                this.tempQualifications[i].name = '';
                this.tempQualifications[i].qualification_id = null;
            }
        },
        selectNationalQual(i, id, name) {
            if (!this.tempQualifications[i]) return;
            if (!id) { this.tempQualifications[i].name = ''; this.tempQualifications[i].qualification_id = null; return; }
            this.tempQualifications[i].qualification_id = parseInt(id, 10);
            this.tempQualifications[i].name = name;
            this.tempQualifications[i].type = '国家資格';
        },
        reorderQual(oldIndex, newIndex) {
            if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
            const arr = this.tempQualifications;
            const item = arr.splice(oldIndex, 1)[0];
            arr.splice(newIndex, 0, item);
            // Sortableが動かしたDOMを破棄し、正しい配列順でAlpineに作り直させる
            const copy = arr.slice();
            this.tempQualifications = [];
            this.$nextTick(() => { this.tempQualifications = copy; });
        },
        updateQual() {
            this.qualifications = JSON.parse(JSON.stringify(this.tempQualifications));
            this.modal.qual = false;
            this.$nextTick(() => this.saveBasicNow());
        },

        // 基本条件モーダルの保存ボタン押下時の即時保存（非Livewire / fetch）。
        // 旧画面と同様、その場でDB保存し、AI生成可否・募集要項の参照表示・トーストを更新する。
        // ※ hidden inputs は Alpine の x-for が再描画してから直列化するため $nextTick 後に呼ぶ。
        saveBasicNow() {
            const payload = (window.recruitSerializeForm && window.recruitSerializeForm()) || {};
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
            fetch('/recruit/basic', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, 'Accept': 'application/json' },
                body: JSON.stringify(payload),
            })
                .then((r) => r.json())
                .then((j) => {
                    if (!j || !j.success) { alert((j && j.message) || '基本条件の保存に失敗しました'); return; }
                    const s = window.Alpine.store('recruitBasicSync');
                    s.aiCanGenerate = !!j.aiCanGenerate;
                    s.aiMessage = j.aiMessage || '';
                    s.category = j.category || '';
                    s.payrollSummary = j.payrollSummary || '';
                    document.dispatchEvent(new CustomEvent('recruit:toast', { detail: { message: j.message || '求人内容を更新しました' } }));
                })
                .catch(() => { alert('基本条件の保存中にエラーが発生しました'); });
        },
    }));
});
